import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clients, team, type Client } from '../lib/api';
import { queryKeys } from './queryKeys';

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useClients(params?: { status?: string; search?: string; page?: number }) {
  return useQuery({
    queryKey: queryKeys.clients.list(params as Record<string, unknown>),
    queryFn: () => clients.list(params),
  });
}

export function useClient(id: number) {
  return useQuery({
    queryKey: queryKeys.clients.detail(id),
    queryFn: () => clients.get(id),
  });
}

export function useTenantOverview(id: number) {
  return useQuery({
    queryKey: [...queryKeys.clients.detail(id), 'overview'],
    queryFn: () => clients.overview(id),
    enabled: Number.isFinite(id),
  });
}

export function useTeam() {
  return useQuery({
    queryKey: queryKeys.team,
    queryFn: () => team.list(),
    staleTime: 5 * 60_000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Client>) => clients.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.clients.all }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Client> }) => clients.update(id, data),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.clients.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.clients.all });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => clients.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.clients.all }),
  });
}

export function useGenerateClientToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, label, expiresDays }: { id: number; label?: string; expiresDays?: number }) =>
      clients.generateToken(id, label, expiresDays),
    onSuccess: (_d, { id }) => qc.invalidateQueries({ queryKey: queryKeys.clients.detail(id) }),
  });
}

export function useSetClientAssignments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userIds }: { id: number; userIds: number[] }) =>
      clients.setAssignments(id, userIds),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.clients.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.clients.all });
    },
  });
}
